import { useEffect, useSyncExternalStore } from 'react'
import { fetchLastReceived, fetchMe } from '../api/client'
import { createStore } from '../lib/store'

/**
 * Detecting that this origin's storage was cleared — and being honest about
 * what that means (design §0.6, §6.3).
 *
 * Architecture §8 asks for lost unsynced entries to be surfaced as "count +
 * affected actions". THAT IS NOT BUILDABLE, and this file does not pretend
 * otherwise. Browser storage eviction is atomic per origin: IndexedDB, Cache
 * Storage and localStorage go together, so the outbox and any counter OF the
 * outbox die in the same event. A count could only be invented.
 *
 * What is buildable is a BOUNDARY. Cookies live in a separate store from
 * origin storage, so a valid session presented alongside a missing install
 * marker is evidence that a clear happened; and the server knows the last
 * command it recorded from this actor (`GET /api/last-received`,
 * `packages/api/src/worker.ts` / `ledger.ts`'s `lastReceivedAt` — an indexed
 * lookup over `events.actor_id` on the tenant's own cell, since `command_log`
 * carries no actor column at all). So the app can say, truthfully:
 *
 *   "This device's stored data was cleared. GridFit last received work from
 *    your account at 14:32 on 1 Aug. Anything this device sent after that —
 *    and anything it never managed to send — was lost."
 *
 * …and link to what the server does hold, so the work can be compared and
 * re-entered. Actionable, which a fabricated count would not be.
 *
 * "YOUR ACCOUNT", NOT "THIS DEVICE" — CORRECTED, review round 1 (CRITICAL):
 * an earlier version of this file said the boundary sentence itself, not
 * just this comment, could keep saying "this device" as a harmless
 * simplification. That was wrong, and wrong in the REASSURING direction,
 * which is worse than merely imprecise. Worked example that broke it: a
 * manager uses a laptop at 09:00 today; their PHONE last synced yesterday at
 * 16:00; the phone's storage clears. `principal.id` is an ACCOUNT identity,
 * identical across every device that account signs into — the server's last
 * received instant for this actor is 09:00 today (the laptop), which is
 * AFTER the phone's own last sync. A sentence claiming "GridFit last
 * received work from THIS DEVICE at 09:00" is false on its face (the phone
 * sent nothing at 09:00), and worse, it understates the boundary: the
 * phone's own actually-lost work was entered BEFORE 16:00 yesterday, well
 * before the stated instant — so a reader concludes nothing of theirs was
 * lost and re-enters nothing. The fix is to say what the server actually
 * knows (`"your account"`, `StorageCleared.tsx`), and to name BOTH ways work
 * from a genuinely offline device can be missing from that instant: sent
 * after it (this device, on some other channel) and never sent at all (an
 * outbox entry that never left this device) — "anything this device sent
 * after that — and anything it never managed to send" covers both without
 * pretending to distinguish them, which the server cannot do either.
 *
 * THE LOAD-BEARING ASSUMPTION IS THE COOKIE ONE, and it is NOT YET VERIFIED
 * on WebKit — see this task's own report and the design doc's §6.3 for the
 * outstanding manual step (a real iOS Safari session, Settings → Safari →
 * Advanced → Website Data). It holds on Chromium and Gecko under quota
 * eviction; WebKit's ITP governs cookies and script-writable storage under
 * partly-overlapping rules that this codebase has not tested against real
 * hardware. If cookies do NOT survive on iOS Safari, the DETECTOR degenerates
 * exactly as designed: no session survives the clear either, so
 * `detectClearance()` below reports `'first-run'` (the least alarming,
 * still-truthful state) rather than `'cleared'` — this half is real and
 * tested (`clearance.test.tsx`'s inconclusive-network and confirmed-absent
 * cases exercise the identical branch a failed cookie assumption would take).
 *
 * WHAT IS **NOT** BUILT, disclosed rather than left implied — review round 1,
 * Important 4: an earlier version of this comment claimed "the honest
 * surface then moves to first sign-in after the clear ... `useSession()`'s
 * ordinary sign-in flow is exactly that surface, and needs no separate
 * implementation." That is false: the sign-in screen says nothing about a
 * possible clear, and `detectClearance()`'s confirmed-absent branch plants
 * the marker the moment it concludes `'first-run'` — BEFORE the user has
 * typed a password, let alone signed in — so by the time sign-in completes
 * there is no signal left to show one against. Only the DEGRADATION half
 * (a failed cookie assumption produces a truthful `'first-run'` rather than
 * a false `'cleared'`) is real. A dedicated one-time notice at first sign-in
 * on a marker-less device — necessarily worded as an ambiguity ("this device
 * is unrecognised, or its data was recently cleared — check for anything you
 * expected to see") since the client genuinely cannot tell those apart once
 * the cookie is gone — is a real, disclosed gap, not built in this task. It
 * would need the marker's write deferred past the moment sign-in completes
 * rather than planted immediately as it is today, which is left for a future
 * task to decide is worth the added complexity.
 */
export type ClearanceStatus = 'intact' | 'cleared' | 'first-run'

/**
 * localStorage, not IndexedDB: this only ever needs to store ONE flag, and
 * localStorage is same-origin storage that clears ATOMICALLY alongside
 * IndexedDB/Cache Storage (§0.6's own premise) — using it costs nothing in
 * fidelity and avoids this module depending on Dexie/`outbox/db.ts` at all
 * (this detector must work even if the outbox itself is what got wiped).
 */
export const INSTALL_MARKER_KEY = 'gridfit.install-marker'

/**
 * Both localStorage accessors are wrapped defensively: Safari's private
 * browsing mode has historically thrown on `setItem` rather than silently
 * no-opping, and a thrown marker WRITE must not crash whatever caller is
 * mid-render because of it — "park, never guess, degrade rather than
 * crash" applied to a browser API gap, the same shape `db.ts`'s
 * `noIndexedDbAvailable` already uses for IndexedDB.
 */
export function hasInstallMarker(): boolean {
  try {
    return localStorage.getItem(INSTALL_MARKER_KEY) !== null
  } catch {
    return false
  }
}

export function writeInstallMarker(): void {
  try {
    localStorage.setItem(INSTALL_MARKER_KEY, '1')
  } catch {
    // Cannot persist — see this function's own doc comment. The caller
    // still gets its return value; the marker simply won't survive to the
    // next boot, which is the same "degraded, not crashed" outcome a
    // browser with no working localStorage would have anyway.
  }
}

/**
 * The three states a boot can resolve to (brief step 1's own three named
 * cases). Collapsing 'first-run' and 'cleared' into one is the exact
 * mutation this file's covering test names: doing so would tell every
 * brand-new user their data was lost, which is both false and alarming.
 *
 * NEVER writes the marker on a CONFIRMED 'cleared' result — see
 * `acknowledgeClearance` below for why: writing it here would erase the
 * only local evidence of what happened before the user has had a chance to
 * see or act on it (a reload — a background tab regaining focus, an iOS
 * memory-pressure reload — would then read 'intact' on the very next boot,
 * silently, which is exactly what architecture §8 forbids).
 *
 * A network failure (offline, or the API unreachable) is NOT evidence
 * either way — `pwa/skew.ts`'s own `checkVersion` treats an unreachable
 * `/api/version` the identical way, for the identical reason. Reported as
 * `'first-run'` for THIS boot only, without writing anything: the least
 * alarming state, and a reversible one, since the very next boot re-runs
 * this check from a clean slate rather than being permanently contaminated
 * by one coincidental blip.
 */
export async function detectClearance(): Promise<ClearanceStatus> {
  if (hasInstallMarker()) return 'intact'

  let sessionConfirmedValid = false
  let sessionConfirmedAbsent = false
  try {
    // `fetchMe` (`api/client.ts`) answers this route's two meaningful
    // outcomes as `null` (a confirmed 401) and a parsed body (ok). Any other
    // STATUS — a 5xx, a 403 — throws and lands in the `catch` below as
    // inconclusive, which is what the previous inline `fetch`'s "any OTHER
    // status" fall-through did too.
    //
    // ONE case genuinely changed, and it is a deliberate improvement rather
    // than a like-for-like move — review round 1's Important 1. A 200 whose
    // BODY IS NOT JSON (a captive portal or an intercepting proxy answering
    // with an HTML page — realistic on the sites this app is used on) used
    // to satisfy the old `else if (response.ok)` branch, because that branch
    // never parsed anything: the detector concluded `'cleared'` and the tray
    // told a site manager their data was lost on the evidence of a wifi
    // login page. `fetchMe` parses, so that response now throws and is
    // treated as inconclusive. A garbage body is not evidence of a live
    // session; `'first-run'` — the least alarming, reversible state, which
    // the very next boot re-examines — is the honest answer to it.
    //
    // `fetchMe`'s single-flight is why `beginClearanceCheck()` no longer
    // costs a SECOND `GET /api/me` alongside `useSession`'s on the boots
    // where this line is reached at all: the two share one request. Not the
    // react-query cache — this function runs from `main.tsx` at module
    // scope, outside React, with no provider anywhere in scope to read a
    // cache from.
    const me = await fetchMe()
    if (me === null) sessionConfirmedAbsent = true
    else sessionConfirmedValid = true
  } catch {
    // Inconclusive — offline, or the API unreachable.
  }

  if (sessionConfirmedValid) {
    // Marker missing + a session the server itself still honours: the ONLY
    // way this combination arises is the cookie surviving an origin-storage
    // clear (§0.6). Left UNWRITTEN — see `acknowledgeClearance`.
    return 'cleared'
  }

  if (sessionConfirmedAbsent) {
    // Genuinely no session AND no marker: a fresh install/browser, not a
    // clear — nothing was ever here to lose. Plant the marker NOW so the
    // next check (very likely moments later, right after this visitor
    // signs in for the very first time) reads 'intact' instead of
    // misreading its own freshly-created session as evidence of a clear.
    writeInstallMarker()
  }

  return 'first-run'
}

/**
 * Module-scope store — `lib/store.ts`'s shared helper as of web-pre-deploy
 * item 9, where this file used to hand-roll the `listeners`/`subscribe`/
 * `getSnapshot` shape itself, citing `pwa/skew.ts` and `outbox/db.ts` as
 * precedent. This module carried TWO such copies (the other is
 * `persistenceStore`, below) and the checklist that motivated the sweep had
 * missed both — see this task's commit message.
 *
 * `'checking'` is the state before the one-time async detection above has
 * resolved — distinct from all three `ClearanceStatus` values so a caller
 * never has to guess whether an early render's `'first-run'` is real or just
 * a not-yet-hydrated default (the same reasoning `db.ts`'s `rowCount` gives
 * for starting at `null` rather than `0`). It is also this store's INITIAL
 * value, which is what `reset()` restores.
 *
 * The `if (status === next) return` guard this file used to write for itself
 * is now `Store.set`'s, unchanged in behaviour: `detectClearance().then` and
 * `acknowledgeClearance()` can both land on the value already held, and
 * neither should re-render the tray for no change.
 */
const statusStore = createStore<ClearanceStatus | 'checking'>('checking')

/**
 * Called from `StorageCleared.tsx`'s "Got it" action — the same "entries
 * persist until repaired or explicitly acknowledged" idiom design §6.2
 * already applies to refused outbox entries (`outbox/repair.tsx`'s
 * `RefusedEntry`/`discard()`), applied here to the one other kind of bad
 * news the tray holds. Plants the marker (ending the 'cleared' state for
 * every future boot) and flips the live store immediately, so the "Lost"
 * section disappears from THIS render without waiting for a reload.
 */
export function acknowledgeClearance(): void {
  writeInstallMarker()
  statusStore.set('intact')
}

/**
 * Guards `detectClearance()` to run at most ONCE per module lifetime (i.e.
 * once per page load) — called from TWO places (both idempotent against
 * each other via this guard): `main.tsx`, unconditionally at boot (review
 * round 1, Important 1 — see this function's own EXPORTED status, below,
 * for why boot-time is the correct moment), and `useClearanceStatus`'s own
 * effect as a fallback for any caller that mounts before `main.tsx`'s call
 * has resolved, or — in this app's OWN test suite — instead of it entirely,
 * since `renderApp()` never imports `main.tsx` at all (the same accepted,
 * pre-existing gap `main.tsx`'s other boot calls already have — see that
 * file's own doc comment).
 *
 * WHY BOOT-TIME MATTERS, not just "some time before the tray is read"
 * (review round 1, Important 1): `SyncTray` — and so, before this fix,
 * `useClearanceStatus()`'s only caller — mounts exclusively inside
 * `AppShell`'s SIGNED-IN branch (`shell/AppShell.tsx`), which itself is
 * never reached for a signed-out visitor at `/login`. A detector that only
 * ever runs from inside that branch can NEVER genuinely observe
 * `'first-run'` in production: by the time it runs, `resolvePrincipal` has
 * already been satisfied, so its own `/api/me` check always finds a valid
 * session, and a brand-new device's very first sign-in would misreport
 * `'cleared'` instead of `'first-run'` — exactly the false alarm this whole
 * file exists to prevent, just reached through a mounting-order bug rather
 * than a logic one. Running from `main.tsx` — before React even renders,
 * let alone before any redirect to `/login` resolves — is what lets a
 * genuinely fresh, signed-out boot see its own true `401` at all.
 */
let checkStarted = false

export function beginClearanceCheck(): void {
  if (checkStarted) return
  checkStarted = true
  void detectClearance().then((next) => statusStore.set(next))
}

/** Reactive read of the module-scope clearance store — mounts the one-time detection on first use (the fallback path described above), mirroring `pwa/skew.ts`'s `useSkewWatch`. */
export function useClearanceStatus(): ClearanceStatus | 'checking' {
  useEffect(() => {
    beginClearanceCheck()
  }, [])
  return useSyncExternalStore(statusStore.subscribe, statusStore.getSnapshot)
}

/**
 * Fetches the boundary figure itself — `GET /api/last-received`
 * (`worker.ts`'s own doc comment has the server-side rationale). A named
 * client call, not `queryHook`: this is a client-only route with no
 * registered query shape behind it, the same reason `useSession`'s
 * `/api/me` read is one too (`auth/session.tsx`'s own doc comment). Callers
 * reach this through react-query's `useQuery` anyway (`StorageCleared.tsx`)
 * — CLAUDE.md's "react-query is the only server cache" — this function is
 * just the `queryFn`, unwrapping `fetchLastReceived`'s object to the bare
 * instant the component actually renders.
 *
 * THROWS on any failure (network error, non-2xx, malformed body) — CORRECTED,
 * review round 1's Important 2: an earlier version of this function swallowed
 * every failure into a `null` return, identical to the server's own genuine
 * "no events recorded" answer. That conflated two entirely different facts —
 * "the actor has no prior work" (a real, honest answer) and "this could not
 * be reached right now" (no answer at all) — and rendered BOTH as "GridFit
 * has no work recorded from this device yet", a definite negative asserted
 * from not knowing, on a device that just lost its storage — i.e. exactly
 * when connectivity is least trustworthy to begin with. Worse, because the
 * `queryFn` never threw, react-query saw a *successful* `null` and never
 * retried, and `StorageCleared`'s `staleTime: Infinity` then locked that
 * wrong answer in for the rest of the session. Throwing here lets
 * react-query's own default retry behaviour run (three retries after the
 * first attempt; `api/queryClient.ts`'s one override reproduces that count
 * exactly for everything except `ResultMismatchError`, which this function
 * cannot produce), and `isError` gives
 * `StorageCleared` a real THIRD state to render honestly, distinct from a
 * confirmed-empty `null`.
 */
export async function fetchLastReceivedAt(): Promise<string | null> {
  const { lastReceivedAt } = await fetchLastReceived()
  return lastReceivedAt
}

/**
 * The outcome of `navigator.storage.persist()` — design §5.4: "requested at
 * install, with the outcome recorded and surfaced in the Sync tray's detail
 * view — a denied request is a materially weaker durability posture and the
 * site manager relying on it deserves to be able to find that out."
 *
 * `'unavailable'` is a FOURTH state, distinct from `'denied'`: a browser
 * with no Storage Manager API at all (jsdom in this test suite; some older
 * WebKit) never asked the question, so telling a site manager their
 * storage was "denied" protection would overclaim a refusal that never
 * happened. Recorded honestly as "we could not even ask", not folded into
 * "denied".
 */
export type PersistenceOutcome = 'granted' | 'denied' | 'unavailable'

/**
 * This module's SECOND store (see `statusStore` above for the shared-helper
 * reasoning that applies to both). `'unknown'` is the pre-answer state —
 * `requestPersistence()` has not run, or has not resolved — and is therefore
 * a further value distinct from the three real outcomes above, for the same
 * reason `'checking'` is distinct from the three `ClearanceStatus` values.
 */
const persistenceStore = createStore<PersistenceOutcome | 'unknown'>('unknown')

/** Reactive read of the persistence-request outcome — the Sync tray's detail view (design §5.4). */
export function usePersistenceOutcome(): PersistenceOutcome | 'unknown' {
  return useSyncExternalStore(persistenceStore.subscribe, persistenceStore.getSnapshot)
}

/**
 * `navigator.storage.persist()` — called once from `main.tsx` "at install"
 * (design §5.4), matching every other one-time boot action that file
 * already runs unconditionally (`startOutboxAutoDrain`, `registerServiceWorker`).
 * Calling it again on a later boot is harmless: the API itself is
 * idempotent (a browser that already decided does not re-prompt), so no
 * "only once ever" guard is needed here the way `checkStarted` above needs
 * one for a network call this file does NOT want to repeat.
 *
 * `navigator.storage?.persist` guarded rather than assumed: jsdom (this
 * whole test suite) and some real browsers have no Storage Manager API at
 * all — see `PersistenceOutcome`'s own doc comment for why that is recorded
 * as `'unavailable'`, a third answer, not folded into `'denied'`.
 *
 * `persist()` ITSELF is called inside a `try`/`catch` — review round 1's
 * Minor, about the exact browser this task concerns: Safari's private
 * browsing mode has historically THROWN out of storage-quota APIs rather
 * than resolving `false` the way a normal refusal does. Uncaught here, that
 * throw would surface as an unhandled promise rejection from a plain
 * `void requestPersistence()` fire-and-forget call (`main.tsx`), which
 * `installGlobalErrorReporting()` (`api/report.ts`) beacons to Workers Logs
 * as a genuine client error — noisy, for an outcome this file already has a
 * name for. Treated as `'denied'`: the practical result is identical either
 * way (this device will not get durable storage), so there is no honesty
 * lost in not inventing a fourth outcome for "asked, and the ASKING itself
 * failed" distinct from "asked, and was told no".
 */
export async function requestPersistence(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.storage?.persist) {
    persistenceStore.set('unavailable')
    return false
  }
  try {
    const granted = await navigator.storage.persist()
    persistenceStore.set(granted ? 'granted' : 'denied')
    return granted
  } catch {
    persistenceStore.set('denied')
    return false
  }
}

/**
 * Test-only. Module-scope state, GENUINELY restored — not the one-way-door
 * shape `outbox/repair.tsx`'s own doc comment warns an earlier task shipped
 * (a registry `.clear()` with nothing to re-populate it, because ESM
 * evaluates a module once). Nothing here is populated by a module-level
 * side effect that a later re-import could fail to re-run: both stores'
 * values and `checkStarted` are plain reassignments, and their listener
 * `Set`s refill on their own the next time a component mounts — there is
 * nothing here for a reset to permanently lose.
 *
 * NAMED FOR WHAT IT DOES, web-pre-deploy item 10 — this is the second of the
 * two `packages/web` resets that do more than the plain, one-way clear the
 * seven `core`/`contracts` `resetXForTests` exports established under that
 * name (the other is `pwa/skew.ts`'s `resetSkewAndSubscribersForTests`, and
 * the third divergent contract, `outbox/drain.ts`'s, is now
 * `detachDrainForTests`). BOTH stores' subscribers go, not just their
 * values, so a component still mounted across the call stops being notified
 * — deliberate, and load-bearing for `test/setup.ts`, which calls this from
 * a global `beforeEach`: a `SyncTray` left subscribed by a previous test
 * would otherwise be re-rendered by THIS test's detection resolving.
 */
export function resetClearanceAndSubscribersForTests(): void {
  statusStore.reset()
  checkStarted = false
  persistenceStore.reset()
  try {
    localStorage.removeItem(INSTALL_MARKER_KEY)
  } catch {
    // See `writeInstallMarker`'s own comment — nothing to undo if it never
    // wrote in the first place.
  }
}
